<template>
  <div
    class="rounded-large bg-surface-container-highest relative h-3"
    :class="[
      scale && !hideLabels && 'my-8.5',
      !scale && !hideLabels && 'mt-4 mb-8',
      !scale && hideLabels && 'my-4',
      scale && hideLabels && 'mb-8',
    ]"
    :style="{
      marginLeft: DOT_SIZE_MATCH / 2 + 'px',
      marginRight: DOT_SIZE_MATCH / 2 + 'px',
    }"
  >
    <template v-for="(dataPoint, index) in dataPoints" :key="index">
      <div
        class="rounded-extra-small absolute left-0 h-full"
        :class="[index == 0 && !isCenterBased && 'rounded-l-large bg-primary']"
        :style="dataPoint.bar"
      >
        <BaseWavyProgressIndicator
          v-if="index != 0"
          class="stroke-primary h-full w-full"
          :thickness="6"
          :wavelength="24"
        />
      </div>
      <div
        class="absolute top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center"
        :style="{
          left: dataPoint.left,
          width: dataPoint.dotSize + 'px',
          height: DOT_SIZE_MATCH + 'px',
        }"
        v-tooltip="!hideNumberOfFeedbacks ? dataPoint.numberOfFeedbacks : undefined"
      >
        <PerspectiveDot
          class="ease-fast-spatial transition-transform duration-200"
          :class="[
            animation >= dataPoint.value - 0.05 && 'scale-100',
            animation < dataPoint.value - 0.05 && !disableAnimation && 'scale-0',
          ]"
          :perspectives="dataPoint.perspectives"
          :topic-perspectives="topicPerspectives"
          :disable-animation="disableAnimation"
        />
      </div>
    </template>
    <PerspectiveValueBarLabels
      v-if="!hideLabels"
      class="absolute z-0 w-full"
      :class="[scale && '-top-8', !scale && '-bottom-4.5']"
      :edge-overflow="DOT_SIZE_MATCH / 2"
      :labels="
        dataPoints.map((dp) => ({
          left: dp.left,
          texts: dp.perspectives.map((p) =>
            resolvePerspectiveLabel(p.key, [p], {
              kind: 'nameShort',
              count: dp.numberOfFeedbacks,
              selfLabel: t('perspectives.self-short', { count: dp.numberOfFeedbacks }),
            }),
          ),
        }))
      "
    />
    <div
      v-if="scale"
      class="text-body-small text-outline absolute right-0 -bottom-8 left-0 mt-2 flex flex-row justify-between"
    >
      <span>{{ scale.items[0].label }}</span>
      <span class="text-right">{{ scale.items[scale.items.length - 1].label }}</span>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, nextTick, ref, watch } from 'vue'
  import BaseWavyProgressIndicator from '../../../../core/components/base/progress-indicator/BaseWavyProgressIndicator.vue'
  import { DO_PERSPECTIVES_MATCH_THRESHOLD } from '../../../models/question-calculation-config'
  import PerspectiveDot from './PerspectiveDot.vue'
  import PerspectiveValueBarLabels from './PerspectiveValueBarLabels.vue'
  import type { Scale } from '@/core/models/scale.ts'
  import { useTransition } from '@vueuse/core'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'

  interface Props {
    perspectives: ReadonlyArray<{
      perspective: Perspective
      average: number | null
      numberOfFeedbacks: number
    }>
    topicPerspectives?: ReadonlyArray<Perspective>
    disableAnimation?: boolean
    scale?: Scale
    isCenterBased?: boolean
    hideLabels?: boolean
    hideNumberOfFeedbacks?: boolean
    animationDelay?: number
  }

  const props = defineProps<Props>()
  const { t } = useI18n()

  const dataPoints = computed(() => {
    const result: {
      value: number
      perspectives: Perspective[]
      numberOfFeedbacks: number
    }[] = []

    for (const entry of props.perspectives) {
      if (typeof entry.average === 'number') {
        result.push({
          value: entry.average,
          perspectives: [entry.perspective],
          numberOfFeedbacks: entry.numberOfFeedbacks,
        })
      }
    }

    const sorted = result.sort((a, b) => a.value - b.value)
    const merged: {
      value: number
      perspectives: Perspective[]
      numberOfFeedbacks: number
    }[] = []

    for (const point of sorted) {
      if (
        merged.length &&
        Math.abs(merged[merged.length - 1]!.value - point.value) <= DO_PERSPECTIVES_MATCH_THRESHOLD
      ) {
        const last = merged[merged.length - 1]!
        last.perspectives.push(...point.perspectives)
        last.value = (last.value + point.value) / 2
        last.numberOfFeedbacks = last.numberOfFeedbacks + point.numberOfFeedbacks
      } else {
        merged.push({ ...point, perspectives: point.perspectives })
      }
    }

    return merged.map((point, idx) => {
      const prevDot = idx > 0 ? merged[idx - 1]! : null
      const prevValue = prevDot ? prevDot.value : 0
      const dotSize = getDotSize(point.perspectives)
      const prevDotSize = prevDot ? getDotSize(prevDot.perspectives) : DOT_SIZE_SINGLE
      return {
        perspectives: point.perspectives,
        dotSize: dotSize,
        value: point.value,
        left: positionLeft(point.value),
        bar: getWidthBetween(
          prevValue,
          props.disableAnimation ? point.value : Math.min(point.value, animation.value),
          dotSize,
          prevDotSize,
          idx == 0,
        ),
        numberOfFeedbacks: point.numberOfFeedbacks,
      }
    })
  })

  const DOT_SIZE_SINGLE = 28
  const DOT_SIZE_SELF = 34
  const DOT_SIZE_MATCH = 42

  const getDotSize = (perspectives: Perspective[]) => {
    if (perspectives.length == 1) {
      if (perspectives.some((p) => p.key === SELF_PERSPECTIVE_KEY)) {
        return DOT_SIZE_SELF
      }
      return DOT_SIZE_SINGLE
    }
    return DOT_SIZE_MATCH
  }

  const maxValue = computed(() => {
    return Math.max(0, ...props.perspectives.map((p) => p.average ?? 0))
  })

  const positionLeft = (value: number) => {
    const percentagePosition = value * 100
    return `${percentagePosition}%`
  }

  const GAP_SIZE = 1
  const getWidthBetween = (
    start: number,
    end: number,
    dotSize: number,
    prevDotSize: number,
    isFirst: boolean,
  ) => {
    const leftPercent = start * 100
    const rightPercent = 100 - end * 100

    let offsetLeft = 0
    if (!isFirst) {
      offsetLeft = prevDotSize / 2 + GAP_SIZE
    }
    const offsetRight = dotSize / 2 + GAP_SIZE

    return {
      left: `calc(${leftPercent}% + ${offsetLeft}px)`,
      right: `calc(${rightPercent}% + ${offsetRight}px)`,
    }
  }

  const ANIMATION_DURATION = 400
  const animationValue = ref(0)

  const animation = useTransition(animationValue, {
    duration: ANIMATION_DURATION,
    transition: [0.34, 0.8, 0.34, 1],
  })

  const delay = computed(() => {
    return Math.min(1500, props.animationDelay ?? 0)
  })

  watch(
    () => props.perspectives,
    async () => {
      animationValue.value = 0
      nextTick(() => {
        setTimeout(() => {
          animationValue.value = maxValue.value
        }, delay.value)
      })
    },
    { immediate: true },
  )
</script>
